/**
 * Bitmart WebSocket Configuration
 *
 * WebSocket channels, topology info, and low-level subscription utilities for Bitmart.
 * @see https://developer-pro.bitmart.com/en/spot/#websocket-market-data
 */

import pako from 'pako';
import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  BitmartWsDepthLevel,
  BitmartWsDepthMessage,
  BitmartWsKlineMessage,
  BitmartWsStatus,
  BitmartWsTickerMessage,
  BitmartWsTradeMessage,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const BITMART_WS_BASE_URL = 'wss://ws-manager-compress.bitmart.com/api?protocol=1.1';

/**
 * Bitmart WebSocket channel descriptors.
 * @see https://developer-pro.bitmart.com/en/spot/#websocket-market-data
 */
export const BITMART_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  depth: {
    channel: 'depth',
    topic: 'spot/depth/increase100:<symbol>',
    supportsBatching: true,
    docUrl: 'https://developer-pro.bitmart.com/en/spot/#public-depth-increase-channel',
  },
  ticker: {
    channel: 'ticker',
    topic: 'spot/ticker:<symbol>',
    supportsBatching: true,
    docUrl: 'https://developer-pro.bitmart.com/en/spot/#public-ticker-channel',
  },
  trades: {
    channel: 'trades',
    topic: 'spot/trade:<symbol>',
    supportsBatching: true,
    docUrl: 'https://developer-pro.bitmart.com/en/spot/#public-trade-channel',
  },
  klines: {
    channel: 'klines',
    topic: 'spot/kline1m:<symbol>',
    supportsBatching: true,
    docUrl: 'https://developer-pro.bitmart.com/en/spot/#public-kline-channel',
  },
};

/**
 * Bitmart WebSocket topology information.
 * Messages can be sent as plain text or binary (zlib raw-compressed).
 */
export const BITMART_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: BITMART_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 10_000,
  note: 'If no message is received within 20s, send a text ping. Binary frames are zlib raw-compressed and must be inflated before JSON parsing.',
};

const DEFAULT_PING_INTERVAL_MS = 10_000;

/**
 * Decode Bitmart WebSocket message (plain or zlib raw-compressed).
 */
export async function decodeBitmartWsMessage(
  event: MessageEvent<ArrayBuffer | Blob | string>,
): Promise<unknown> {
  if (typeof event.data === 'string') {
    const trimmed = event.data.trim();
    if (trimmed === 'pong') {
      return { event: 'pong' };
    }
    if (trimmed === 'ping') {
      return { event: 'ping' };
    }
    try {
      return JSON.parse(event.data);
    } catch {
      // If it's not JSON, return as is (could be a plain string from server)
      return event.data;
    }
  }

  try {
    const buffer = event.data instanceof Blob ? await event.data.arrayBuffer() : event.data;
    const text = pako.inflateRaw(new Uint8Array(buffer), { to: 'string' });
    return JSON.parse(text);
  } catch (error) {
    // If inflate fails, try to parse as plain text (maybe it's not compressed)
    try {
      const buffer = event.data instanceof Blob ? await event.data.arrayBuffer() : event.data;
      const text = new TextDecoder().decode(buffer);
      return JSON.parse(text);
    } catch {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Bitmart WebSocket decode error: ${message}`);
    }
  }
}

interface BitmartWsSubscriptionOptions<T> {
  topic: string;
  onMessage: (data: T) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BitmartWsStatus) => void;
  baseUrl?: string;
  pingIntervalMs?: number;
  wsConfig?: {
    maxRetries?: number;
    minReconnectionDelay?: number;
  };
}

interface BitmartWsErrorResponse {
  event?: string;
  errorCode?: string;
  errorMessage?: string;
}

const KLINE_CHANNELS: Record<string, string> = {
  '1m': 'kline1m',
  '5m': 'kline5m',
  '15m': 'kline15m',
  '30m': 'kline30m',
  '1h': 'kline1H',
  '2h': 'kline2H',
  '4h': 'kline4H',
  '1d': 'kline1D',
  '1w': 'kline1W',
  '1M': 'kline1M',
};

const resolveBitmartKlineChannel = (interval: string): string => {
  const normalized = interval.trim();
  const direct = KLINE_CHANNELS[normalized];
  if (direct) {
    return direct;
  }

  const upper = normalized.toUpperCase();
  if (upper.endsWith('M') && normalized.endsWith('M')) {
    return KLINE_CHANNELS['1M'];
  }

  return KLINE_CHANNELS['1m'];
};

const createBitmartWsSubscription = <T>(
  options: BitmartWsSubscriptionOptions<T>,
): ReconnectingWebSocket => {
  const {
    topic,
    onMessage,
    onError,
    onStatusChange,
    baseUrl = BITMART_WS_BASE_URL,
    pingIntervalMs = DEFAULT_PING_INTERVAL_MS,
    wsConfig = {},
  } = options;

  const config = {
    maxRetries: wsConfig.maxRetries ?? 10,
    minReconnectionDelay: wsConfig.minReconnectionDelay ?? 1000,
  };

  let hasConnectedOnce = false;
  let lastMessageAt = Date.now();
  let pingTimer: ReturnType<typeof setInterval> | null = null;

  onStatusChange?.('connecting');

  const ws = new ReconnectingWebSocket(baseUrl, [], config);
  ws.binaryType = 'arraybuffer';

  const clearPingTimer = () => {
    if (pingTimer) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  };

  const startPingTimer = () => {
    clearPingTimer();
    pingTimer = setInterval(() => {
      const now = Date.now();
      if (now - lastMessageAt >= pingIntervalMs) {
        ws.send('ping');
        lastMessageAt = now;
      }
    }, pingIntervalMs);
  };

  ws.addEventListener('open', () => {
    hasConnectedOnce = true;
    onStatusChange?.('connected');
    lastMessageAt = Date.now();
    startPingTimer();

    ws.send(
      JSON.stringify({
        op: 'subscribe',
        args: [topic],
      }),
    );
  });

  ws.addEventListener('message', (event: MessageEvent) => {
    void (async () => {
      try {
        const payload = await decodeBitmartWsMessage(event);
        lastMessageAt = Date.now();

        if (payload && typeof payload === 'object') {
          const maybeError = payload as BitmartWsErrorResponse;
          if (maybeError.errorCode) {
            onError?.(
              new Error(
                `Bitmart WS error ${maybeError.errorCode}: ${maybeError.errorMessage ?? 'Unknown error'}`,
              ),
            );
            return;
          }

          if ((payload as { event?: string }).event === 'pong') {
            return;
          }

          if ((payload as { event?: string }).event === 'ping') {
            ws.send('pong');
            return;
          }
        }

        if (payload && (payload as { event?: string }).event === 'subscribe') {
          return;
        }

        onMessage(payload as T);
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error(String(error)));
        onStatusChange?.('error');
      }
    })();
  });

  ws.addEventListener('close', () => {
    clearPingTimer();
    onStatusChange?.(hasConnectedOnce ? 'reconnecting' : 'disconnected');
  });

  ws.addEventListener('error', event => {
    onError?.(new Error(`Bitmart WebSocket error: ${event.type}`));
    onStatusChange?.('error');
  });

  return ws;
};

export function subscribeBitmartTicker(params: {
  symbol: string;
  onMessage: (data: BitmartWsTickerMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BitmartWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;

  return createBitmartWsSubscription<BitmartWsTickerMessage>({
    topic: `spot/ticker:${symbol}`,
    onMessage,
    onError,
    onStatusChange,
  });
}

export function subscribeBitmartTrades(params: {
  symbol: string;
  onMessage: (data: BitmartWsTradeMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BitmartWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;

  return createBitmartWsSubscription<BitmartWsTradeMessage>({
    topic: `spot/trade:${symbol}`,
    onMessage,
    onError,
    onStatusChange,
  });
}

export function subscribeBitmartKlines(params: {
  symbol: string;
  interval: string;
  onMessage: (data: BitmartWsKlineMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BitmartWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, interval, onMessage, onError, onStatusChange } = params;
  const channel = resolveBitmartKlineChannel(interval);

  return createBitmartWsSubscription<BitmartWsKlineMessage>({
    topic: `spot/${channel}:${symbol}`,
    onMessage,
    onError,
    onStatusChange,
  });
}

export function subscribeBitmartDepth(params: {
  symbol: string;
  depth?: 5 | 20 | 50 | 100 | 200;
  onMessage: (data: BitmartWsDepthMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BitmartWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, depth = 100, onMessage, onError, onStatusChange } = params;

  // spot/depth5, spot/depth20, spot/depth50 are snapshot channels
  // spot/depth/increase100, spot/depth/increase200 are incremental channels
  const isIncremental = depth > 50;
  const topic = isIncremental
    ? `spot/depth/increase${depth}:${symbol}`
    : `spot/depth${depth}:${symbol}`;

  // Local state for incremental updates
  const localAsks = new Map<string, string>();
  const localBids = new Map<string, string>();

  const normalizeLevel = (level: BitmartWsDepthLevel): [string, string] => {
    if (Array.isArray(level)) {
      return [String(level[0]), String(level[1])];
    }
    return [level.price, level.amount];
  };

  return createBitmartWsSubscription<BitmartWsDepthMessage>({
    topic,
    onMessage: (message: BitmartWsDepthMessage) => {
      const entry = message.data?.[0];
      if (!entry) {
        return;
      }

      // For non-incremental channels, just pass the message through
      if (!isIncremental) {
        onMessage(message);
        return;
      }

      // Handle incremental updates
      if (entry.type === 'snapshot') {
        localAsks.clear();
        localBids.clear();
      }

      // Apply changes: amount "0" means remove the level
      for (const level of entry.asks) {
        const [price, amount] = normalizeLevel(level);
        if (amount === '0' || amount === '0.0' || !amount) {
          localAsks.delete(price);
        } else {
          localAsks.set(price, amount);
        }
      }

      for (const level of entry.bids) {
        const [price, amount] = normalizeLevel(level);
        if (amount === '0' || amount === '0.0' || !amount) {
          localBids.delete(price);
        } else {
          localBids.set(price, amount);
        }
      }

      // Generate a full snapshot from the local state
      const sortedAsks = Array.from(localAsks.entries())
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .slice(0, depth);

      const sortedBids = Array.from(localBids.entries())
        .sort((a, b) => Number(b[0]) - Number(a[0]))
        .slice(0, depth);

      const accumulatedMessage: BitmartWsDepthMessage = {
        ...message,
        data: [
          {
            ...entry,
            asks: sortedAsks.map(([price, amount]) => ({ price, amount })),
            bids: sortedBids.map(([price, amount]) => ({ price, amount })),
            type: 'snapshot', // Treat as snapshot for the consumer
          },
        ],
      };

      onMessage(accumulatedMessage);
    },
    onError,
    onStatusChange,
  });
}
